import AlertCircle from "@untitled-ui/icons-react/build/esm/AlertCircle";
import AlertTriangle from "@untitled-ui/icons-react/build/esm/AlertTriangle";
import HelpCircle from "@untitled-ui/icons-react/build/esm/HelpCircle";
import Plus from "@untitled-ui/icons-react/build/esm/Plus";
import Users03 from "@untitled-ui/icons-react/build/esm/Users03";
import { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import PopupContext from "../../../../../PopupContext";
import * as Library from "../../../../../utils/Library";
import BtnLargeSolid from "../../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../../common/checkbox/CheckboxVector";
import FormField from "../../../../common/forms/FormField";
import SimpleLoadingSpinner from "../../../../common/loaders/SimpleLoadingSpinner";
import FullScreenOverlay from "../../../../common/overlay/FullScreenOverlay";
import { isMissingSourceValue, sourceValuesMatch } from "../sourceMappingIdentity";
import RadioButtonVector from "../../../../common/radioButton/RadioButtonVector";
import TooltipWithInfoCircle from "../../../../common/tooltip/TooltipWithInfoCircle";
import { DialogWithIcon } from "../../POSExportPage";

// #region SCOPED STYLES
const StyledGendersTablePopupV2 = styled.div`
    & {
        width: 570px;
        text-align: start;
    }

    .table-list-simple {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }

    .popup-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        align-self: stretch;
        width: 100%;
        padding-top: 9px;
        padding-bottom: 21px;
        border-bottom: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
        margin-bottom: 8px;
    }

    .header-title-container {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .header-icon {
        width: 16px;
        height: 16px;
        color: var(--text-and-icons-green-500, #00bb00);
    }

    .header-title {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: 14px;
        font-weight: 400;
        text-transform: uppercase;
        position: relative;
        top: 1px;
    }

    .header-actions {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
    }

    .popup-content {
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        width: 100%;
    }

    .bulk-action-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
        justify-content: flex-start;
        align-self: stretch;
        flex-shrink: 0;
    }

    .bulk-action-wrapper {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        height: 27px;
    }

    .bulk-action-bar {
        border-radius: 4px;
        padding: 8px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: flex-start;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
    }

    .bulk-action-row {
        display: flex;
        flex-direction: row;
        gap: 9px;
        align-items: center;
        justify-content: flex-start;
        flex: 1;
    }

    .bulk-label {
        color: var(--text-and-icons-gray-300, #4f5b6d);
        text-align: left;
        font-size: 14px;
        font-weight: 400;
    }

    .input-field-container {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        border-radius: 4px;
        padding: 6px 8px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex: 1;
        height: 27px;
    }

    .bulk-add-btn {
        background: var(--buttons-all-platforms-gray-500, #10141b);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 29px;
        height: 27px;
        flex-shrink: 0;
    }

    .bulk-add-btn.enabled:hover {
        box-shadow: 0px 0px 0px 1px #ff8c00;
    }

    .plus {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        overflow: visible;
    }

    .table-row {
        padding: 8px 8px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        align-self: stretch;
        flex-shrink: 0;
    }

    .checkbox-cell {
        padding: 0px 16px 0px 0px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex-shrink: 0;
        width: 46px;
    }

    .brand-name-header {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        justify-content: flex-start;
        flex-shrink: 0;
        width: 241px;
    }

    .brand-name-cell {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        justify-content: flex-start;
        flex: 1;
    }

    .item-name {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: 14px;
        font-weight: 400;
    }

    .pos-value-header {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        justify-content: flex-start;
        flex: 1;
    }

    .pos-label {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: 14px;
        font-weight: 400;
    }

    .divider,
    .divider-2 {
        margin-top: -1px;
        border-style: solid;
        border-color: var(--backgrounds-and-lines-n-100, #3c485a);
        border-width: 1px 0 0 0;
        flex-shrink: 0;
        width: 100%;
        height: 0px;
    }

    .divider-2 {
        border-color: #262e3e;
    }

    .table-scroll-area {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: flex-start;
        justify-content: flex-start;
        align-self: stretch;
        flex-shrink: 0;
        overflow-y: auto;
        max-height: 700px;
    }

    .table-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        justify-content: flex-start;
        flex: 1;
    }

    .table-item {
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: flex-start;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        //height: 35px;
    }

    .table-item.even {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
    }

    .save-warning {
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        align-self: stretch;
    }

    .empty-genders-state {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
        align-self: stretch;
        padding: 4px 0px 8px 0px;
    }

    .empty-genders-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .empty-genders-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }

    .empty-genders-description {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
        line-height: 16px;
    }

    /* Keep the natural height, with room to scroll on short viewports. */
    & {
        display: flex;
        flex-direction: column;
        min-height: 0;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
    }

    .table-list-simple {
        min-height: 0;
        overflow-y: auto;
    }

    .table-list-simple > * {
        flex-shrink: 0;
    }

    .table-scroll-area {
        flex-shrink: 1;
        /* Retain a readable scroll target if controls exceed the viewport. */
        min-height: 44px;
    }

    .table-body {
        align-self: flex-start;
    }

    .table-body > * {
        flex-shrink: 0;
    }
`;

const StyledMissingGendersSection = styled.div`
    padding-top: ${({ onlyMissingGenders }) => (onlyMissingGenders ? "0px" : "8px")};
    display: flex;
    flex-direction: column;
    gap: 0px;

    .divider {
        margin-top: -1px;
        border-style: solid;
        border-color: var(--backgrounds-and-lines-n-100, #3c485a);
        border-width: 1px 0 0 0;
        width: 100%;
        height: 0px;
    }

    .header-row {
        display: flex;
        align-items: center;
        gap: 6px;
        color: var(--text-and-icons-orange-500, #ff9900);
        font-family: "Roboto-SemiBold", sans-serif;
        font-size: 12px;
        font-weight: 600;

        .help-circle {
            width: 16px;
            height: 16px;
        }
    }

    .description {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-family: "Roboto-Regular", sans-serif;
        font-size: 14px;
        line-height: normal;
        font-weight: 400;
    }

    .radio-group {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: center;
        padding-top: 4px;

        .radio-option {
            display: flex;
            align-items: center;
            gap: 6px;
            cursor: pointer;

            span {
                color: var(--text-and-icons-gray-100, #d1d1d1);
                font-family: "Roboto-Regular", sans-serif;
                font-size: 14px;
                font-weight: 400;
            }

            &.disabled {
                cursor: not-allowed;
                span {
                    color: var(--text-and-icons-gray-300, #4f5b6d);
                }
            }
        }
    }

    .pos-gender-input-row {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
        border-radius: 4px;
        //padding: 5px 8px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        height: 35px;

        .label {
            color: var(--text-and-icons-gray-100, #d1d1d1);
            font-family: "Roboto-Regular", sans-serif;
            font-size: 12px;
            font-weight: 400;
        }

        .input-container {
            background: var(--backgrounds-and-lines-n-600, #10141b);
            border-radius: 4px;
            display: flex;
            align-items: center;
            width: 240px;
            height: 27px;
            padding: 0;
            //overflow: hidden;
        }
    }
`;
// #endregion

export default function POSGendersTablePopup({
    show,
    onClose,
    genderMappings,
    setGenderMappings,
    setSaveButtonEnabled,
    posExportRequestData
}) {
    // #region VARS
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const [hideMainPopup, setHideMainPopup] = useState(false);
    const [bulkText, setBulkText] = useState("");
    const [bulkChangeBtnEnabled, setBulkChangeBtnEnabled] = useState(false);

    const [newGenderMappings, setNewGenderMappings] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const [missingGenderRule, setMissingGenderRule] = useState("leave_empty");
    const [missingGenderPosValue, setMissingGenderPosValue] = useState("");
    // #endregion

    // #region COMPUTED VALUES AND MEMOS
    const { labelBrandProducts = [], labelBrandGenders = [], validGendersForPOS = [] } = posExportRequestData || {};

    const showNoDataMessage = !labelBrandProducts?.length;

    const posGenders = useMemo(() => {
        return (labelBrandGenders || []).map(item => ({
            value: item?.name ?? "Not provided",
            name: item?.name ?? "Not provided",
            total: item?.total,
            selected: false
        }));
    }, [labelBrandGenders]);

    const validGendersOptions = useMemo(() => {
        return (validGendersForPOS || []).map(item => {
            const name = item === "" ? "Leave empty / Unknown" : item;

            return {
                name,
                value: name
            };
        });
    }, [validGendersForPOS]);

    const initialMissingRule = useMemo(() => {
        const notProvidedMapping = genderMappings?.find(
            g => isMissingSourceValue(g.originalGenderName)
        );
        //return notProvidedMapping && notProvidedMapping.userGenderName !== "" ? "specify" : "leave_empty";
        return notProvidedMapping ? "specify" : "leave_empty";
    }, [genderMappings]);

    const initialMissingPosValue = useMemo(() => {
        const notProvidedMapping = genderMappings?.find(
            g => isMissingSourceValue(g.originalGenderName)
        );
        //return notProvidedMapping ? notProvidedMapping.userGenderName : "";
        return notProvidedMapping?.userGenderName;
    }, [genderMappings]);

    const missingGendersItems = useMemo(() => {
        return posGenders?.filter(g => {
            const name = g?.name;
            return isMissingSourceValue(name);
        });
    }, [posGenders]);

    const missingCount = missingGendersItems?.reduce((acc, curr) => acc + (curr?.total || 0), 0) || 0;
    const showMissingGenders = missingCount > 0;

    const missingName = useMemo(() => {
        if (!missingGendersItems || missingGendersItems.length === 0) return null;
        return missingGendersItems.some(g => g.name === "No data provided") ? "No data provided" : "Not provided";
    }, [missingGendersItems]);

    const hasChecked = newGenderMappings.some(s => s.isChecked);
    const allChecked = newGenderMappings.length > 0 && newGenderMappings.every(s => s.isChecked);

    const isDirty = useMemo(() => {
        const mappingsDirty = newGenderMappings.some(newS => {
            const oldS = genderMappings?.find(s => s.originalGenderName === newS.originalGenderName);
            const newName = newS.userGenderName?.trim();
            const oldName = oldS?.userGenderName?.trim();
            return newName !== oldName;
        });

        const ruleDirty = missingGenderRule !== initialMissingRule;
        const posValueDirty =
            missingGenderRule === "specify" && missingGenderPosValue?.trim() !== initialMissingPosValue?.trim();

        return mappingsDirty || ruleDirty || posValueDirty;
    }, [
        newGenderMappings,
        genderMappings,
        missingGenderRule,
        missingGenderPosValue,
        initialMissingRule,
        initialMissingPosValue
    ]);
    const allGendersMapped = newGenderMappings.every(m => !!m.userGenderName?.trim());
    const missingGendersBlockSave =
        showMissingGenders && missingGenderRule === "specify" && !missingGenderPosValue?.trim();

    const isSaveEnabled = useMemo(() => {
        if (showNoDataMessage || hasChecked || !isDirty) return false;
        if (!allGendersMapped) return false;
        if (missingGendersBlockSave) return false;

        return true;
    }, [showNoDataMessage, hasChecked, isDirty, allGendersMapped, missingGendersBlockSave]);
    const showSaveWarning = !showNoDataMessage && (hasChecked || !allGendersMapped || missingGendersBlockSave);

    const isBulkChangeBtnEnabled = bulkChangeBtnEnabled && hasChecked;

    const onlyMissingGenders = newGenderMappings.length === 0 && showMissingGenders;
    // #endregion

    // #region METHODS
    const showSuccessToast = () => {
        Library.showToastMessage({
            type: "success",
            title: "All genders linked!",
            description: "All brand’s gender are linked."
        });
    };

    // eslint-disable-next-line no-unused-vars
    const showErrorToast = () => {
        Library.showToastMessage({
            type: "error",
            title: "Unable to save!",
            description: "Something went wrong and we were unable to save. Please try again."
        });
    };

    const handleCloseClicked = () => {
        if (isDirty) {
            setHideMainPopup(true);
            Library.showConfirmationMessage({
                setGenericConfirmationPopup,
                useV2: true,
                headingText: "ATTENTION!!",
                headingColor: "var(--text-icons-orange-500, #FF9800)",
                title: (
                    <DialogWithIcon className="frame-160">
                        <AlertTriangle className="help-circle" style={{ color: "#FF9800" }} />
                        <div className="frame-286">
                            <div className="title">Leaving without saving</div>
                        </div>
                    </DialogWithIcon>
                ),
                messageHtml: "You are leaving without saving.<br/>Confirm you want to proceed",
                confirmText: "PROCEED",
                cancelText: "CANCEL",
                confirmState: "secondary",
                cancelState: "secondary",
                confirmTextColor: "var(--buttons-all-platforms-orange-500, #FF9800)",
                onConfirmClicked: () => {
                    setHideMainPopup(false);
                    onClose();
                },
                onCancelClicked: () => {
                    setHideMainPopup(false);
                }
            });
        } else {
            setHideMainPopup(false);
            onClose();
        }
    };

    const onSaveClicked = () => {
        if (!isSaveEnabled || isSaving) return;

        setIsSaving(true);
        setTimeout(() => {
            const mappingsToSave = newGenderMappings
                .filter(s => s.userGenderName?.trim() !== "")
                .map(s => ({
                    originalGenderName: s.originalGenderName,
                    userGenderName: s.userGenderName?.trim()
                }));

            if (showMissingGenders && missingGendersItems) {
                if (missingGenderRule === "specify" && missingGenderPosValue?.trim() !== "") {
                    missingGendersItems.forEach(item => {
                        mappingsToSave.push({
                            originalGenderName: item.name,
                            userGenderName: missingGenderPosValue?.trim()
                        });
                    });
                }
            }

            setGenderMappings(mappingsToSave);

            if (setSaveButtonEnabled) {
                setSaveButtonEnabled(true);
            }

            setIsSaving(false);
            showSuccessToast();
            setHideMainPopup(false);
            onClose();
        }, 800);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (!show) return;

        setMissingGenderRule(initialMissingRule);
        setMissingGenderPosValue(initialMissingPosValue);

        const mappedGenders = posGenders
            .filter(g => !isMissingSourceValue(g?.name))
            .map(posGenderObj => {
                const genderName = posGenderObj?.name;
                const genderTotal = posGenderObj?.total;
                const genderMapping = genderMappings?.find(
                    genderMapping => sourceValuesMatch(genderMapping?.originalGenderName, genderName)
                );

                return {
                    originalGenderName: genderName,
                    userGenderName: genderMapping?.userGenderName,
                    total: genderTotal,
                    isChecked: false
                };
            });

        setNewGenderMappings(mappedGenders);
        setBulkChangeBtnEnabled(false);
    }, [show]);
    // #endregion

    // #region BULK ADD LOGIC
    const handleBulkAdd = () => {
        if (!bulkChangeBtnEnabled || !hasChecked) return;

        setNewGenderMappings(prev =>
            prev.map(gender => {
                if (gender.isChecked) {
                    return { ...gender, userGenderName: bulkText, isChecked: false };
                }
                return gender;
            })
        );

        // Clear bulk text
        setBulkText("");
        setBulkChangeBtnEnabled(false);
    };
    // #endregion

    // #region RENDER
    return (
        <FullScreenOverlay show={show && !hideMainPopup}>
            <StyledGendersTablePopupV2 className="genders-table-popup">
                <div className="table-list-simple">
                    <div className="popup-header">
                        <div className="header-title-container">
                            <Users03 className="header-icon" />
                            <div className="header-title">CUSTOMIZE GENDERS</div>
                        </div>
                        <div className="header-actions">
                            <BtnLargeSolid
                                title="CLOSE"
                                onClick={handleCloseClicked}
                                normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverBorderColor="var(--text-icons-orange-500)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase"
                                }}
                                style={{
                                    width: "67px",
                                    height: "30px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                            <BtnLargeSolid
                                title={isSaving ? <SimpleLoadingSpinner /> : "SAVE"}
                                onClick={onSaveClicked}
                                disabled={!isSaveEnabled || isSaving}
                                normalColor="var(--buttons-all-platforms-orange-500, #FF9800)"
                                hoverColor="var(--buttons-all-platforms-orange-400, #FF9800)"
                                disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    color:
                                        !isSaveEnabled || isSaving
                                            ? "var(--text-and-icons-gray-300, #4f5b6d)"
                                            : "#ffffff"
                                }}
                                style={{
                                    width: "67px",
                                    height: "30px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                        </div>
                    </div>

                    {showNoDataMessage && (
                        <div className="empty-genders-state">
                            <div className="empty-genders-title">
                                <AlertCircle className="empty-genders-icon" />
                                <span>NO DATA YET</span>
                            </div>
                            <div className="empty-genders-description">
                                This could be a new brand in BOtag. Once the first items from this brand are available,
                                you can link your standard Genders to the brand's listed genders.
                            </div>
                        </div>
                    )}

                    {!showNoDataMessage && (
                        <>
                            <div className={`popup-content ${onlyMissingGenders ? "d-none" : ""}`}>
                                <div className="bulk-action-section">
                                    <div className="bulk-action-wrapper">
                                        <div className="bulk-action-bar">
                                            <div className="bulk-action-row">
                                                <div
                                                    className="bulk-label"
                                                    style={{
                                                        flexShrink: 0,
                                                        color: hasChecked
                                                            ? "var(--text-and-icons-gray-100, #d1d1d1)"
                                                            : "var(--text-and-icons-gray-300, #4f5b6d)"
                                                    }}>
                                                    Map selected Brand genders to POS gender:
                                                </div>
                                                <div
                                                    className="input-field-container"
                                                    style={{
                                                        padding: 0,
                                                        flexShrink: 1,
                                                        width: "auto",
                                                        background: "transparent"
                                                    }}>
                                                    <FormField
                                                        type="dropdown"
                                                        dropdownWithinViewport={true}
                                                        varName="gender-bulk-change"
                                                        styling={{
                                                            formFieldWidth: "100%",
                                                            formFieldHeight: "27px",
                                                            marginBottom: "0px",
                                                            dropdownOverlayTop: "35px"
                                                        }}
                                                        placeholder="Select yours"
                                                        isDropdownMultiselect={false}
                                                        isDropdownSearchable={true}
                                                        dropdownData={validGendersOptions}
                                                        value={hasChecked ? bulkText : ""}
                                                        onDropdownChange={item => {
                                                            setBulkText(item.name);
                                                            setBulkChangeBtnEnabled(true);
                                                        }}
                                                        requiredType={hasChecked ? "simple" : "disabledSimple"}
                                                        useDropdownPortal={true}
                                                    />
                                                </div>

                                                <div
                                                    className={`bulk-add-btn ${isBulkChangeBtnEnabled ? "enabled cursor-pointer" : "disabled hover-not-allowed"}`}
                                                    onClick={isBulkChangeBtnEnabled ? handleBulkAdd : null}>
                                                    <Plus
                                                        className="plus"
                                                        color={
                                                            isBulkChangeBtnEnabled
                                                                ? "#FFFFFF"
                                                                : "var(--text-and-icons-gray-300, #7C868B)"
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {showSaveWarning && (
                                        <div className="save-warning">
                                            SAVE is only enabled when all brand's genders are linked to a POS gender!
                                        </div>
                                    )}
                                </div>
                                <div className="table-row">
                                    <div className="checkbox-cell">
                                        <CheckboxVector
                                            checkboxColor="#00F260"
                                            onCheckboxClick={() => {
                                                const newValue = !allChecked;
                                                setNewGenderMappings(prev =>
                                                    prev.map(s => ({ ...s, isChecked: newValue }))
                                                );
                                            }}
                                            isChecked={allChecked}
                                        />
                                    </div>
                                    <div className="brand-name-header">
                                        <div className="brand-name-cell">
                                            <div className="item-name">Brand's genders</div>
                                            <TooltipWithInfoCircle
                                                iconSize={"14px"}
                                                enabled={true}
                                                header="Brand's genders"
                                                title={
                                                    <>
                                                        These are the gender values provided by the Label Brand in the
                                                        product master.
                                                        <br />
                                                        Each brand gender must be linked to a POS gender before the
                                                        gender rules can be saved.
                                                        <br />
                                                        <br />
                                                        This does not change the original gender value in BOtag. It only
                                                        defines how products with this brand gender should be exported
                                                        to Front Systems.
                                                    </>
                                                }
                                                width="350px"
                                            />
                                        </div>
                                    </div>
                                    <div className="pos-value-header">
                                        <div className="pos-label">Genders to POS</div>
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="Genders to POS"
                                            title={
                                                <>
                                                    Select which Front Systems gender this brand gender should be
                                                    exported as.
                                                    <br />
                                                    The dropdown shows the gender options supported by Front Systems.
                                                    <br />
                                                    Each brand gender must be linked to a POS gender before the gender
                                                    rules can be saved.
                                                    <br />
                                                    <br />
                                                    This does not change the original gender in BOtag. It only controls
                                                    the gender value sent to Front Systems.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                </div>
                                <div className="divider-2"></div>
                            </div>

                            <div className={`table-scroll-area ${onlyMissingGenders ? "d-none" : ""}`}>
                                <div className="table-body">
                                    {newGenderMappings.map((gender, idx) => (
                                        <div className={`table-item ${idx % 2 === 0 ? "even" : ""}`} key={idx}>
                                            <div className="table-row">
                                                <div className="checkbox-cell">
                                                    <CheckboxVector
                                                        checkboxColor="#00F260"
                                                        onCheckboxClick={() => {
                                                            setNewGenderMappings(prev =>
                                                                prev.map((s, i) =>
                                                                    i === idx ? { ...s, isChecked: !s.isChecked } : s
                                                                )
                                                            );
                                                        }}
                                                        isChecked={gender.isChecked || false}
                                                    />
                                                </div>
                                                <div className="brand-name-cell">
                                                    <div className="item-name">
                                                        {gender.originalGenderName}
                                                        {/* {gender.total !== undefined && gender.total !== null
                                                            ? ` (${gender.total})`
                                                            : ""} */}
                                                    </div>
                                                </div>
                                                <div
                                                    className="input-field-container"
                                                    style={{ padding: 0, background: "transparent" }}>
                                                    <FormField
                                                        type="dropdown"
                                                        dropdownWithinViewport={true}
                                                        varName={`gender-dropdown-${idx}`}
                                                        styling={{
                                                            formFieldWidth: "100%",
                                                            formFieldHeight: "27px",
                                                            marginBottom: "0px",
                                                            dropdownOverlayTop: "35px"
                                                        }}
                                                        placeholder="Add yours"
                                                        isDropdownMultiselect={false}
                                                        isDropdownSearchable={true}
                                                        dropdownData={validGendersOptions}
                                                        value={
                                                            gender.userGenderName === ""
                                                                ? "Leave empty / Unknown"
                                                                : gender.userGenderName
                                                        }
                                                        onDropdownChange={item => {
                                                            setNewGenderMappings(prev =>
                                                                prev.map((s, i) =>
                                                                    i === idx ? { ...s, userGenderName: item.name } : s
                                                                )
                                                            );
                                                        }}
                                                        useDropdownPortal={true}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`missing-genders-section ${!showMissingGenders ? "d-none" : ""}`}>
                                {showMissingGenders && (
                                    <MissingGendersSection
                                        count={missingCount}
                                        rule={missingGenderRule}
                                        setRule={setMissingGenderRule}
                                        posValue={missingGenderPosValue}
                                        setPosValue={setMissingGenderPosValue}
                                        onlyMissingGenders={onlyMissingGenders}
                                        missingName={missingName}
                                        validGendersOptions={validGendersOptions}
                                    />
                                )}
                            </div>

                            <div className="d-none divider" style={{ marginTop: showMissingGenders ? "8px" : "0px" }} />
                        </>
                    )}
                </div>
            </StyledGendersTablePopupV2>
        </FullScreenOverlay>
    );
    // #endregion
}

// #region SMALL COMPONENTS
const MissingGendersSection = ({
    count,
    rule,
    setRule,
    posValue,
    setPosValue,
    onlyMissingGenders,
    missingName,
    validGendersOptions
}) => {
    return (
        <StyledMissingGendersSection
            className="missing-genders-section-container"
            onlyMissingGenders={onlyMissingGenders}>
            {!onlyMissingGenders && <div className="divider" />}

            <div
                className="header-row px-8px"
                style={{
                    paddingTop: onlyMissingGenders ? "8px" : "13px",
                    paddingBottom: onlyMissingGenders ? "0px" : "0px"
                }}>
                <HelpCircle className="help-circle" color="#FF9900" />
                <span>MISSING GENDERS ({count})</span>
            </div>

            {onlyMissingGenders && missingName === "Not provided" ? (
                <>
                    <div
                        className="orange-text px-8px"
                        style={{
                            color: "var(--text-and-icons-orange-500, #ff9900)",
                            fontFamily: "Roboto-Regular, sans-serif",
                            fontSize: "14px",
                            lineHeight: "20px",
                            textAlign: "center",
                            paddingBottom: "12px",
                            paddingTop: "6px"
                        }}>
                        No gender field was found in this brand's product file.
                    </div>
                    <div className="description px-8px" style={{ paddingBottom: "16px" }}>
                        You can still decide how BOtag should export products when gender is missing.
                        <br />
                        BOtag will keep the brand's original data unchanged.
                    </div>
                </>
            ) : (
                <div
                    className={`description px-8px ${onlyMissingGenders ? "" : "py-8px"}`}
                    style={
                        onlyMissingGenders
                            ? { paddingBottom: "16px", paddingTop: "8px" }
                            : { paddingTop: "8px", paddingBottom: "0px" }
                    }>
                    One or more products from this supplier do not include gender. Choose how BOtag should handle them
                    when exporting to your POS.
                </div>
            )}

            <div className="radio-group px-8px">
                <div className="radio-option" onClick={() => setRule("leave_empty")}>
                    <RadioButtonVector
                        isSelected={rule === "leave_empty"}
                        radioColor={rule === "leave_empty" ? "#00bb00" : "#7c868b"}
                    />
                    <span>Leave empty</span>
                </div>

                <div className="radio-option" onClick={() => setRule("specify")}>
                    <RadioButtonVector
                        isSelected={rule === "specify"}
                        radioColor={rule === "specify" ? "#00bb00" : "#7c868b"}
                    />
                    <span>Use one POS gender for all missing values</span>
                </div>

                <div className="radio-option disabled">
                    <RadioButtonVector isSelected={false} disabled={true} />
                    <span>Use rules</span>
                </div>
            </div>

            <>
                <div className="divider d-none" style={{ display: rule === "specify" ? "flex" : "none" }} />

                <div
                    className="pos-gender-input-row px-8px mt-16px  py-8px"
                    style={{ display: rule === "specify" ? "flex" : "none" }}>
                    <div className="label">When not provided by the brand, show in POS as:</div>
                    <div className="input-container">
                        <FormField
                            type="dropdown"
                            dropdownWithinViewport={true}
                            varName="missing-gender-input"
                            styling={{
                                formFieldWidth: "100%",
                                formFieldHeight: "27px",
                                marginBottom: "0px",
                                dropdownOverlayTop: "35px"
                            }}
                            placeholder="Add yours"
                            isDropdownMultiselect={false}
                            isDropdownSearchable={true}
                            dropdownData={validGendersOptions}
                            value={posValue === "" ? "Leave empty / Unknown" : posValue}
                            onDropdownChange={item => {
                                setPosValue(item.name);
                            }}
                            disabled={rule !== "specify"}
                        />
                    </div>
                </div>
            </>
        </StyledMissingGendersSection>
    );
};
// #endregion
